import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"
import { showHubTeamToast } from "../utils/hub_team_toast"
import { rosterSelection, selectionFormData, selectedMemberCount, reloadRoster } from "../utils/roster_selection"

const MEMBERS_LIST_CLASS = "hub-team-batch-invite-breakdown--members"

const COPY = {
  invite: {
    title: "Send invitations",
    action: "Send invitations",
    nobody: "Nobody in this selection is waiting on a first invitation.",
    breakdown: [
      ["Will be emailed", "eligible_count"],
      ["Already invited, will not be emailed again", "awaiting_acceptance_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  },
  schedule: {
    title: "Schedule invitations",
    action: "Schedule invitations",
    nobody: "Nobody in this selection is waiting on a first invitation.",
    breakdown: [
      ["Will be scheduled", "not_invited_count"],
      ["Already scheduled, date will be updated", "scheduled_count"],
      ["Already invited, will not be emailed again", "awaiting_acceptance_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  },
  // Staff ticking members on the company record: each one gets what they still need. The keys
  // name the member groups the preview returns, listed by name under each heading.
  invite_selected: {
    title: "Send invitations to selected members",
    action: "Invite selected",
    nobody: "Everyone you selected has already accepted their invitation.",
    breakdown: [
      ["Will get a first invitation", "invite"],
      ["Scheduled, will be sent now instead", "scheduled"],
      ["Already invited, will get a reminder", "remind"],
      ["Already accepted, will not be emailed", "accepted"]
    ]
  },
  remind: {
    title: "Send a reminder",
    action: "Send reminders",
    nobody: "Nobody in this selection is waiting to accept an invitation.",
    breakdown: [
      ["Will be reminded", "eligible_count"],
      ["Not invited yet, will not be emailed", "not_invited_count"],
      ["Scheduled, will not be emailed", "scheduled_count"],
      ["Already accepted, will not be emailed", "accepted_count"]
    ]
  }
}

/**
 * Batch Invite Modal Controller
 *
 * Confirms a batch send against a count the server worked out for the real selection — which may
 * be "everyone matching these filters" — rather than the number of rows on screen, then reports
 * back how many went out and who was skipped or failed.
 */
export default class extends Controller {
  static targets = [
    "modal",
    "title",
    "description",
    "breakdown",
    "confirmation",
    "results",
    "resultsSummary",
    "outcomes",
    "submitButton",
    "cancelButton",
    "lineTemplate",
    "timingFields",
    "timingInput",
    "dateField",
    "dateInput"
  ]

  static values = { inviteUrl: String, remindUrl: String, selectedUrl: String, previewUrl: String }

  async open(event) {
    event.preventDefault()

    const trigger = event.currentTarget.dataset
    this.mode = trigger.batchInviteMode || "invite"
    // "pending" scope (the staff company page header) has no row selection: the server counts and
    // emails everyone still awaiting a first invitation. "row" scope is one member's own action.
    this.rosterScope = trigger.batchInviteScope || "selection"
    this.rowMember = this.rosterScope === "row"
      ? { id: trigger.batchInviteCompanyUserId, name: trigger.batchInviteMemberName }
      : null
    if (this.rosterScope === "selection" && selectedMemberCount(this) === 0) return

    this.resetTiming(trigger.batchInviteScheduledOn)
    this.showConfirmation()
    Modal.getOrCreateInstance(this.modalTarget).show()
    await this.loadPreview()
  }

  timingChanged() {
    const later = this.schedulingLater()

    if (this.hasDateFieldTarget) this.dateFieldTarget.hidden = !later
    if (this.hasDateInputTarget && !later) this.dateInputTarget.value = ""

    if (this.preview) this.renderBreakdown(this.preview)
  }

  async loadPreview() {
    const formData = this.selectionFormData()
    formData.append("mode", this.mode)

    const data = await this.post(this.previewUrlValue, formData, "Could not count the selection")
    if (!data) return this.close()

    this.eligibleCount = data.eligible_count
    this.preview = data
    this.renderBreakdown(data)
  }

  async send() {
    if (!this.eligibleCount) return
    if (this.schedulingLater() && !this.selectedDate()) return

    this.setSending(true)
    const formData = this.selectionFormData()
    if (this.schedulingLater()) formData.append("invitation_scheduled_on", this.selectedDate())

    const data = await this.post(this.sendUrl(), formData, this.failurePrefix())
    this.setSending(false)

    if (!data) return

    this.renderResults(data)
    this.refreshRoster()
  }

  // --- rendering ---------------------------------------------------------------

  showConfirmation() {
    this.eligibleCount = null
    this.preview = null
    this.titleTarget.textContent = this.copy().title
    this.descriptionTarget.textContent = this.mode === "invite"
      ? "Counting who will be invited..."
      : "Counting who will be emailed..."
    this.breakdownTarget.hidden = true
    this.confirmationTarget.hidden = false
    this.resultsTarget.hidden = true
    this.outcomesTarget.hidden = true
    this.submitButtonTarget.hidden = false
    this.submitButtonTarget.disabled = true
    this.submitButtonTarget.textContent = this.copy().action
    this.cancelButtonTarget.textContent = "Cancel"
    this.toggleTimingFields()
  }

  renderBreakdown(preview) {
    const eligible = preview.eligible_count
    const verb = this.schedulingLater() ? "scheduled" : "emailed"

    this.descriptionTarget.textContent = eligible === 0
      ? this.copy().nobody
      : this.eligibleDescription(eligible, preview.selected_count, verb)

    if (preview.members) {
      this.renderMemberGroups(preview.members)
    } else {
      this.replaceLines(this.breakdownTarget, this.copy().breakdown
        .filter(([, key]) => preview[key] > 0)
        .map(([label, key]) => [label, String(preview[key])]))
    }

    const canSubmit = eligible > 0 && (!this.schedulingLater() || Boolean(this.selectedDate()))
    this.submitButtonTarget.disabled = !canSubmit
    this.submitButtonTarget.textContent = eligible === 0 ? this.copy().action : `${this.copy().action} (${eligible})`
  }

  renderResults(result) {
    const skipped = result.skipped || []
    const failed = result.failed || []
    const scheduled = Boolean(result.scheduled)

    this.confirmationTarget.hidden = true
    this.resultsTarget.hidden = false
    this.submitButtonTarget.hidden = true
    this.cancelButtonTarget.textContent = "Done"

    if (scheduled) {
      this.resultsSummaryTarget.textContent = `${this.memberPhrase(result.scheduled_count)} scheduled.`
    } else {
      this.resultsSummaryTarget.textContent = failed.length === 0
        ? `${this.sentPhrase(result)}.`
        : `${this.sentPhrase(result)}, ${failed.length} could not be sent.`
    }

    this.replaceLines(this.outcomesTarget, [
      ...failed.map((entry) => [entry.name || entry.email, entry.reason]),
      ...skipped.map((entry) => [entry.name || entry.email, entry.reason])
    ])

    if (failed.length === 0 && skipped.length === 0) {
      showHubTeamToast(this.resultsSummaryTarget.textContent)
    }
  }

  replaceLines(list, lines) {
    list.replaceChildren(...lines.map(([label, value]) => this.line(label, value)))
    list.classList.remove(MEMBERS_LIST_CLASS)
    list.hidden = lines.length === 0
  }

  // Each group heading carries its count, and the members it covers are listed under it by name.
  renderMemberGroups(members) {
    const lines = this.copy().breakdown
      .filter(([, key]) => members[key]?.length > 0)
      .flatMap(([label, key]) => [
        this.line(label, String(members[key].length), "hub-team-batch-invite-line--group"),
        ...members[key].map((member) => this.memberLine(member))
      ])

    this.breakdownTarget.replaceChildren(...lines)
    this.breakdownTarget.classList.add(MEMBERS_LIST_CLASS)
    this.breakdownTarget.hidden = lines.length === 0
  }

  // A long name or address is cut short on a narrow modal, so the whole line is its tooltip.
  memberLine(member) {
    const line = this.line(member.name || member.email, member.name ? member.email : "", "hub-team-batch-invite-line--member")
    line.title = [member.name, member.email].filter(Boolean).join(" · ")
    return line
  }

  line(label, value, modifier = null) {
    const line = this.lineTemplateTarget.content.firstElementChild.cloneNode(true)
    if (modifier) line.classList.add(modifier)
    line.querySelector(".hub-team-batch-invite-line__label").textContent = label
    line.querySelector(".hub-team-batch-invite-line__value").textContent = value
    return line
  }

  // --- plumbing ----------------------------------------------------------------

  eligibleDescription(eligible, selected, verb) {
    if (this.rosterScope === "pending") {
      return `${this.memberPhrase(eligible)} awaiting invitation will be ${verb}.`
    }

    if (this.rowMember) {
      return `A fresh invitation will be ${verb} to ${this.rowMember.name}.`
    }

    return `${eligible} of the ${this.memberPhrase(selected)} you selected will be ${verb}.`
  }

  // The header's "pending" send never describes a selection: the server emails everyone still
  // awaiting a first invitation, whatever boxes happen to be ticked on the page.
  selectionFormData() {
    if (this.rosterScope === "pending") return new FormData()

    return selectionFormData(this, this.rowMember ? [this.rowMember.id] : null)
  }

  copy() {
    if (this.mode === "remind") return COPY.remind
    if (this.mode === "invite_selected") return COPY.invite_selected
    if (this.schedulingLater()) return COPY.schedule

    return COPY.invite
  }

  sendUrl() {
    if (this.mode === "remind") return this.remindUrlValue
    if (this.mode === "invite_selected") return this.selectedUrlValue

    return this.inviteUrlValue
  }

  failurePrefix() {
    if (this.mode === "remind") return "Failed to send reminders"
    if (this.schedulingLater()) return "Failed to schedule invitations"

    return "Failed to send invitations"
  }

  schedulingLater() {
    if (this.mode !== "invite" || !this.hasTimingInputTarget) return false

    return this.timingInputTargets.some((input) => input.checked && input.value === "later")
  }

  selectedDate() {
    return this.hasDateInputTarget ? this.dateInputTarget.value : ""
  }

  toggleTimingFields() {
    if (!this.hasTimingFieldsTarget) return

    this.timingFieldsTarget.hidden = this.mode !== "invite"
    this.timingChanged()
  }

  // A row whose invitation is already scheduled opens on that date, so changing it starts from
  // what is set rather than from "Send now".
  resetTiming(scheduledOn = "") {
    const timing = scheduledOn ? "later" : "now"

    if (this.hasTimingInputTarget) {
      this.timingInputTargets.forEach((input) => {
        input.checked = input.value === timing
      })
    }

    if (this.hasDateInputTarget) this.dateInputTarget.value = scheduledOn
    if (this.hasDateFieldTarget) this.dateFieldTarget.hidden = !scheduledOn
  }

  // A selection send reports first invitations and reminders apart; every other send just counts
  // who was emailed.
  sentPhrase(result) {
    const invited = result.invited_count
    const reminded = result.reminded_count

    if (invited === undefined || invited + reminded === 0) return `${this.memberPhrase(result.sent_count)} emailed`
    if (reminded === 0) return `${this.memberPhrase(invited)} invited`
    if (invited === 0) return `${this.memberPhrase(reminded)} reminded`

    return `${this.memberPhrase(invited)} invited, ${reminded} reminded`
  }

  memberPhrase(count) {
    return count === 1 ? "1 member" : `${count} members`
  }

  setSending(sending) {
    this.submitButtonTarget.disabled = sending
    if (sending) this.submitButtonTarget.textContent = "Sending..."
  }

  close() {
    Modal.getOrCreateInstance(this.modalTarget).hide()
  }

  // A send changes status badges, the awaiting-invitation count and the row actions at once, so
  // the frame is reloaded rather than those states being reproduced in JavaScript. The staff
  // company page has no roster frame (and no Turbo), so it reloads once the results are dismissed.
  refreshRoster() {
    if (rosterSelection(this)) return reloadRoster(this)

    this.modalTarget.addEventListener("hidden.bs.modal", () => window.location.reload(), { once: true })
  }

  async post(url, formData, errorPrefix) {
    try {
      const response = await fetch(url, {
        method: "POST",
        body: formData,
        headers: {
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content,
          "Accept": "application/json"
        },
        credentials: "same-origin"
      })

      const data = await response.json()
      if (response.ok && data.success) return data

      showHubTeamToast(`${errorPrefix}: ${data?.errors?.join(", ") || `HTTP ${response.status}`}`, {
        variant: "error"
      })
    } catch (error) {
      console.error(errorPrefix, error)
      showHubTeamToast("An error occurred. Please try again.", { variant: "error" })
    }

    return null
  }
}
